import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Paper,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { fetchCurrentUser } from '../userSlice';
import OnboardingLayout from './OnboardingLayout';
import SubscriptionSetup from './SubscriptionSetup';
import onboardingService from '../services/onboardingService';
import { getOrganizationDetails } from '../services/organizationService';

export default function OnboardingBillingStep() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);
  const [continueLoading, setContinueLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [onboardingData, orgData] = await Promise.all([
          onboardingService.getOnboardingStatus(),
          getOrganizationDetails()
        ]);
        
        setOnboardingStatus(onboardingData);
        setOrganization(orgData);
        
        // Check if organization has an active subscription
        const hasActiveSubscription = orgData?.subscription?.isActive && orgData?.subscription?.subscriptionId;
        setHasExistingSubscription(hasActiveSubscription);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data');
      } finally {
        setInitialLoading(false);
      }
    };

    fetchData();
  }, []);

  // Refresh organization data when component becomes visible (e.g., when navigating from room creation)
  useEffect(() => {
    const refreshOrganizationData = async () => {
      if (!initialLoading) {
        try {
          const orgData = await getOrganizationDetails();
          setOrganization(orgData);
          
          // Update subscription status
          const hasActiveSubscription = orgData?.subscription?.isActive && orgData?.subscription?.subscriptionId;
          setHasExistingSubscription(hasActiveSubscription);
        } catch (error) {
          console.error('Error refreshing organization data:', error);
        }
      }
    };

    // Add a small delay to ensure any backend updates have been processed
    const timeoutId = setTimeout(refreshOrganizationData, 500);
    return () => clearTimeout(timeoutId);
  }, [initialLoading]);

  const handleSubscriptionSuccess = async (subscription) => {
    try {
      // Mark billing step as completed
      await onboardingService.updateOnboardingStep('billing', 'complete');
      
      // Refresh user data to get updated onboarding status
      await dispatch(fetchCurrentUser());
      
      // Navigate to next step
      navigate('/onboarding/complete');
    } catch (error) {
      console.error('Error completing billing step:', error);
      setError('Failed to complete billing setup');
    }
  };

  const handleContinueWithExistingSubscription = async () => {
    setContinueLoading(true);
    setError('');

    try {
      // Mark billing step as completed
      await onboardingService.updateOnboardingStep('billing', 'complete');
      
      // Refresh user data to get updated onboarding status
      await dispatch(fetchCurrentUser());
      
      // Navigate to next step
      navigate('/onboarding/complete');
    } catch (error) {
      console.error('Error completing billing step:', error);
      setError('Failed to continue');
    } finally {
      setContinueLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <OnboardingLayout
        currentStep="billing"
        completedSteps={[]}
        title="Loading..."
      >
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      currentStep="billing"
      completedSteps={onboardingStatus?.completedSteps || []}
      skippedSteps={onboardingStatus?.skippedSteps || []}
      title={hasExistingSubscription ? "Your Subscription is Active!" : "Set Up Your Billing"}
      subtitle={hasExistingSubscription ? "Great! You have an active subscription. Review your billing details below and continue to the next step." : "Subscribe to our service to start managing your meeting rooms. Simple, transparent pricing."}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Subscription Status for Existing Subscriptions */}
      {hasExistingSubscription && organization && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'success.50' }}>
          <Typography variant="h6" color="success.main" gutterBottom>
            ✅ Subscription Active
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your subscription is set up and active! Here are your current billing details:
          </Typography>
          
          <Box sx={{ pl: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • <strong>Current Rooms:</strong> {organization.subscription?.currentRoomCount || 0} room(s)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • <strong>Monthly Cost:</strong> ${(organization.subscription?.currentRoomCount || 0) * 5}/month
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              • <strong>Rate:</strong> $5 per room per month
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • <strong>Status:</strong> Active and ready for use
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Pricing Info - Only show for new subscriptions */}
      {!hasExistingSubscription && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
          <Typography variant="h6" color="primary" gutterBottom>
            💳 Simple, Transparent Pricing
          </Typography>
          <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
            $5 <Typography component="span" variant="body1">per room per month</Typography>
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your first payment will be prorated based on your current room count.
          </Typography>

          {/* Features List */}
          <List dense>
            {[
              'Unlimited bookings per room',
              'Team member invitations',
              'Calendar integrations',
              'Email notifications',
              'Advanced reporting',
              '24/7 customer support'
            ].map((feature, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle color="success" fontSize="small" />
                </ListItemIcon>
                <ListItemText 
                  primary={feature} 
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Current Room Count */}
      {organization && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Usage
            </Typography>
            <Typography variant="body1" color="text.secondary">
              You currently have <strong>{organization.subscription?.currentRoomCount || 0} room(s)</strong> in your organization.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Your monthly cost will be: <strong>${(organization.subscription?.currentRoomCount || 0) * 5}</strong>
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Subscription Setup - Only show for new subscriptions */}
      {organization && !hasExistingSubscription && (
        <SubscriptionSetup 
          organizationId={organization._id}
          onSuccess={handleSubscriptionSuccess}
        />
      )}

      {/* Continue Button - Show for existing subscriptions */}
      {hasExistingSubscription && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleContinueWithExistingSubscription}
            disabled={continueLoading}
            sx={{ minWidth: 150 }}
          >
            {continueLoading ? 'Continuing...' : 'Continue'}
          </Button>
        </Box>
      )}

      {/* Help Text */}
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {hasExistingSubscription 
            ? 'You can manage your subscription and billing details anytime from your organization settings.'
            : 'Secure payments powered by Stripe. Cancel anytime from your account settings.'
          }
        </Typography>
      </Box>
    </OnboardingLayout>
  );
} 